import { useStateMachine } from '@heidi/flow/react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { mintTurnId, type TurnOperation } from '../../../contracts';
import {
  createDeferredTurnOperation,
  type DeferredTurnOperation,
} from '../../harness/turn-operation-registry';
import {
  projectsComposerDraftDisplayText,
  type ProjectsComposerPromptDraft,
} from '../projects-composer-editor-draft';
import type { SubmitProjectTurnArgs } from '../submit-project-turn-args';
import { rebuildQueuedTurnFromBodyEdit } from './queued-turn-edit';
import {
  initialTurnCommandContext,
  isTurnCommandBusy,
  turnCommandGuards,
  turnCommandMachine,
  worldEventFor,
  type ComposerTurn,
  type ProjectChatEnqueueUx,
  type QueuedComposerTurn,
  type TurnCommandContext,
  type TurnCommandEffect,
  type TurnCommandEvent,
  type TurnCommandStateValue,
  type TurnCommandWorld,
} from './turn-command.machine';

export type {
  ComposerTurn,
  ProjectChatEnqueueUx,
  QueuedComposerTurn,
} from './turn-command.machine';

/** Sole public edit-eligibility helper for Work composition. */
export { getQueuedTurnEditableBody } from './queued-turn-edit';

const toSubmitArgs = (turn: ComposerTurn): SubmitProjectTurnArgs => ({
  text: turn.text,
  routineName: turn.routineName,
  displayText: turn.displayText,
  files: turn.files,
  attachment_ids: turn.attachment_ids,
  optimisticAttachments: turn.optimisticAttachments,
  computerUseSkill: turn.computerUseSkill,
  analytics: turn.analytics,
  notePushPlan: turn.notePushPlan,
  restoreContext: turn.restoreContext ?? {
    draft: turn.draft,
    linkedItems: turn.linkedItems,
  },
  approvalMode: turn.approvalMode,
  turnId: turn.turnId,
  failureUxOwner: turn.failureUxOwner,
});

const toComposerTurn = (command: SubmitProjectTurnArgs): ComposerTurn => {
  const text = command.text;
  const draft = command.restoreContext?.draft ?? {
    text: command.displayText ?? text,
    segments: [],
  };
  return {
    ...command,
    turnId: command.turnId || mintTurnId(),
    draft,
    previewText: command.displayText ?? text,
    linkedItems: command.restoreContext?.linkedItems ?? [],
  };
};

export const COMPOSER_SEND_QUEUE_UX: ProjectChatEnqueueUx = {
  toast: true,
  clearComposer: true,
  remember: true,
};

export const FOLLOW_UP_QUEUE_UX: ProjectChatEnqueueUx = {
  toast: true,
  clearComposer: false,
  remember: false,
};

export const CONNECT_QUEUE_UX: ProjectChatEnqueueUx = {
  toast: false,
  clearComposer: false,
  remember: false,
};

interface UseQueuedTurnsArgs {
  cancelTurn: () => void;
  clearComposer: (options?: { refocus?: boolean }) => void;
  draft: ProjectsComposerPromptDraft;
  isStartingSeededTurn: boolean;
  isSubmitting: boolean;
  onHasQueuedTurnsChange?: (hasQueuedTurns: boolean) => void;
  onScrollTranscript?: () => void;
  rememberLastSentTurn: (turn: ComposerTurn) => void;
  /** Starts one registered operation. The invoke awaits `operation.terminal`. */
  startTurn: (command: SubmitProjectTurnArgs) => TurnOperation;
  turnInFlight: boolean;
  onTurnAccepted?: (turn: ComposerTurn) => void;
  onTurnRejected?: (turn: ComposerTurn, error: unknown) => void;
  /** App-side enqueue reaction. Copy/toast stay at the composition point. */
  onTurnQueued?: (turn: ComposerTurn, ux: ProjectChatEnqueueUx) => void;
}

export interface QueuedTurns {
  queuedTurns: QueuedComposerTurn[];
  /** Busy from the send click itself: rises synchronously with the command,
   *  falls one commit after the world settles — never between the two. */
  isCommandBusy: boolean;
  handleSendNow: (queued: QueuedComposerTurn) => TurnOperation;
  removeQueuedTurn: (id: string) => void;
  editQueuedTurn: (id: string, content: string) => void;
  /**
   * Sets the edit hold on a queued message. Leaving edit resumes automatic
   * drain when the world is already quiet and the head is no longer held.
   */
  setQueuedEditing: (id: string, isEditing: boolean) => void;
  /** Kill-switch card Clear all — ghost UI never surfaces this. */
  clearQueuedTurns: () => void;
  /** Promote a loose turn the same way as a queued "Send now" action. */
  sendTurnNow: (turn: ComposerTurn) => TurnOperation;
  sendOrQueueTurn: (turn: ComposerTurn, ux?: ProjectChatEnqueueUx) => TurnOperation;
  /** Parent publishers enqueue and receive a correlated operation. */
  enqueueTurn: (
    command: SubmitProjectTurnArgs,
    ux?: ProjectChatEnqueueUx
  ) => TurnOperation;
  enqueueTurnNow: (command: SubmitProjectTurnArgs) => TurnOperation;
}

/**
 * Binds the composer to the `turnCommand` machine. The invoked
 * `startTurnAndWaitForTerminal` service is the hold: nothing else POSTs until
 * that exact operation's terminal settles.
 *
 * Effects on this channel are only enqueue UX and interrupt (`cancelTurn`).
 * Dispatch is the invoke, not an inspect-run POST.
 */
export const useQueuedTurns = ({
  cancelTurn,
  clearComposer,
  draft,
  isStartingSeededTurn,
  isSubmitting,
  onHasQueuedTurnsChange,
  onScrollTranscript,
  rememberLastSentTurn,
  startTurn,
  turnInFlight,
  onTurnAccepted,
  onTurnRejected,
  onTurnQueued,
}: UseQueuedTurnsArgs): QueuedTurns => {
  const contextRef = useRef<TurnCommandContext>(initialTurnCommandContext);
  const performedEffectRef = useRef(0);
  const sendRef = useRef<(event: TurnCommandEvent) => void>(() => undefined);
  const deferredByTurnIdRef = useRef(new Map<string, DeferredTurnOperation>());

  const adoptDeferred = useCallback((turnId: string, operation: TurnOperation) => {
    const deferred = deferredByTurnIdRef.current.get(turnId);
    if (!deferred) return;
    deferredByTurnIdRef.current.delete(turnId);
    deferred.adopt(operation);
  }, []);

  const abandonDeferred = useCallback((turnId: string, error?: unknown) => {
    const deferred = deferredByTurnIdRef.current.get(turnId);
    if (!deferred) return;
    deferredByTurnIdRef.current.delete(turnId);
    deferred.abandon('withdrawn', error);
  }, []);

  const ensureDeferred = useCallback((turn: ComposerTurn): TurnOperation => {
    const existing = deferredByTurnIdRef.current.get(turn.turnId);
    if (existing) return existing.operation;
    const deferred = createDeferredTurnOperation(turn.turnId);
    deferredByTurnIdRef.current.set(turn.turnId, deferred);
    return deferred.operation;
  }, []);

  const isTurnOwned = useCallback((turnId: string): boolean => {
    const context = contextRef.current;
    return (
      context.activeCommand?.turnId === turnId ||
      context.queue.some((item) => item.turn.turnId === turnId)
    );
  }, []);

  const performEffect = useCallback(
    (effect: TurnCommandEffect) => {
      if (effect.kind === 'interrupt') {
        cancelTurn();
        return;
      }
      if (effect.ux.remember) rememberLastSentTurn(effect.turn);
      if (effect.ux.clearComposer) clearComposer();
      onScrollTranscript?.();
      onTurnQueued?.(effect.turn, effect.ux);
    },
    [cancelTurn, clearComposer, onScrollTranscript, onTurnQueued, rememberLastSentTurn]
  );

  const [state, send] = useStateMachine<
    TurnCommandContext,
    TurnCommandStateValue,
    TurnCommandEvent
  >(turnCommandMachine, {
    guards: turnCommandGuards,
    services: {
      startTurnAndWaitForTerminal: (context) => {
        const command = context.activeCommand;
        if (!command) {
          return Promise.resolve({
            kind: 'abandoned' as const,
            turnId: null,
            reason: 'withdrawn' as const,
          });
        }
        const operation = startTurn(toSubmitArgs(command));
        adoptDeferred(command.turnId, operation);
        void operation.accepted
          .then(() => onTurnAccepted?.(command))
          .catch((error: unknown) => onTurnRejected?.(command, error));
        return operation.terminal;
      },
    },
    inspect: (record, context) => {
      contextRef.current = context;
      if (record.to === 'runningOwnedTurn' && record.from !== 'runningOwnedTurn') {
        const command = context.activeCommand;
        if (command) {
          rememberLastSentTurn(command);
          onScrollTranscript?.();
          if (context.ownsComposerDraft) {
            clearComposer();
          }
        }
      }
      if (record.to === 'readyToDispatch') {
        const world: TurnCommandWorld = {
          turnInFlight,
          isSubmitting,
          isStartingSeededTurn,
        };
        sendRef.current({
          type: worldEventFor(world, context),
          world,
        });
      }
      if (context.effect === null || context.effectSeq === performedEffectRef.current) {
        return;
      }
      performedEffectRef.current = context.effectSeq;
      performEffect(context.effect);
    },
  });

  sendRef.current = send;

  const readWorld = useCallback(
    (): TurnCommandWorld => ({
      turnInFlight,
      isSubmitting,
      isStartingSeededTurn,
    }),
    [isStartingSeededTurn, isSubmitting, turnInFlight]
  );

  const syncWorld = useCallback((): TurnCommandWorld => {
    const world = readWorld();
    const context = contextRef.current;
    send({ type: worldEventFor(world, context), world });
    return world;
  }, [readWorld, send]);

  const queuedTurns = state.context.queue;

  useEffect(() => {
    syncWorld();
  }, [queuedTurns.length, syncWorld]);

  useEffect(() => {
    send({
      type: 'DRAFT_CHANGED',
      fingerprint: projectsComposerDraftDisplayText(draft) ?? draft.text,
    });
  }, [draft, send]);

  useLayoutEffect(() => {
    onHasQueuedTurnsChange?.(queuedTurns.length > 0);
  }, [onHasQueuedTurnsChange, queuedTurns.length]);

  useEffect(() => {
    const deferredByTurnId = deferredByTurnIdRef;
    return () => {
      for (const deferred of deferredByTurnId.current.values()) {
        deferred.abandon('unmounted');
      }
      deferredByTurnId.current.clear();
    };
  }, []);

  const sendNow = useCallback(
    (id: string | null, turn: ComposerTurn): TurnOperation => {
      const operation = ensureDeferred(turn);
      const world = readWorld();
      send({
        type: 'SEND_NOW',
        id,
        turn,
        world,
      });
      if (!isTurnOwned(turn.turnId)) {
        abandonDeferred(turn.turnId);
      }
      return operation;
    },
    [abandonDeferred, ensureDeferred, isTurnOwned, readWorld, send]
  );

  const handleSendNow = useCallback(
    (queued: QueuedComposerTurn) => sendNow(queued.id, queued.turn),
    [sendNow]
  );

  const sendTurnNow = useCallback((turn: ComposerTurn) => sendNow(null, turn), [sendNow]);

  const sendOrQueueTurn = useCallback(
    (
      turn: ComposerTurn,
      ux: ProjectChatEnqueueUx = COMPOSER_SEND_QUEUE_UX
    ): TurnOperation => {
      const operation = ensureDeferred(turn);
      const world = syncWorld();
      send({ type: 'SEND', turn, ux, world });
      if (!isTurnOwned(turn.turnId)) {
        abandonDeferred(turn.turnId);
      }
      return operation;
    },
    [abandonDeferred, ensureDeferred, isTurnOwned, send, syncWorld]
  );

  const enqueueTurn = useCallback(
    (
      command: SubmitProjectTurnArgs,
      ux: ProjectChatEnqueueUx = CONNECT_QUEUE_UX
    ): TurnOperation => {
      const turnId = command.turnId || mintTurnId();
      return sendOrQueueTurn(toComposerTurn({ ...command, turnId }), ux);
    },
    [sendOrQueueTurn]
  );

  const enqueueTurnNow = useCallback(
    (command: SubmitProjectTurnArgs): TurnOperation => {
      const turnId = command.turnId || mintTurnId();
      const turn = toComposerTurn({ ...command, turnId });
      const operation = ensureDeferred(turn);
      const world = readWorld();
      send({
        type: world.isStartingSeededTurn ? 'TAKE_OWNED_NOW' : 'SEND_NOW',
        id: null,
        turn,
        world,
      });
      if (!isTurnOwned(turn.turnId)) {
        abandonDeferred(turn.turnId);
      }
      return operation;
    },
    [abandonDeferred, ensureDeferred, isTurnOwned, readWorld, send]
  );

  const removeQueuedTurn = useCallback(
    (id: string) => {
      const item = contextRef.current.queue.find((queued) => queued.id === id);
      send({ type: 'REMOVE_QUEUED', id });
      if (item) abandonDeferred(item.turn.turnId);
    },
    [abandonDeferred, send]
  );

  const editQueuedTurn = useCallback(
    (id: string, content: string) => {
      const item = contextRef.current.queue.find((queued) => queued.id === id);
      if (!item) return;
      const nextTurn = rebuildQueuedTurnFromBodyEdit(item.turn, content);
      if (!nextTurn) return;
      send({ type: 'UPDATE_QUEUED', id, turn: nextTurn });
      // Save ends edit; re-sync so a quiet world can drain the head.
      syncWorld();
    },
    [send, syncWorld]
  );

  const setQueuedEditing = useCallback(
    (id: string, isEditing: boolean) => {
      send({ type: 'SET_QUEUED_EDITING', id, isEditing });
      if (!isEditing) {
        syncWorld();
      }
    },
    [send, syncWorld]
  );

  const clearQueuedTurns = useCallback(() => {
    const pending = [...contextRef.current.queue];
    send({ type: 'CLEAR_QUEUE' });
    for (const item of pending) {
      abandonDeferred(item.turn.turnId);
    }
  }, [abandonDeferred, send]);

  return {
    queuedTurns,
    isCommandBusy: isTurnCommandBusy(state),
    handleSendNow,
    removeQueuedTurn,
    editQueuedTurn,
    setQueuedEditing,
    clearQueuedTurns,
    sendTurnNow,
    sendOrQueueTurn,
    enqueueTurn,
    enqueueTurnNow,
  };
};
